import { http } from './http';
import { ApiCall } from '@/api/ApiCall';
import {FilterParam, PaginatedResponse} from '@/utils/DataSet';
import { KeyValue } from '@/types/Common';
import NewRecordResponse = App.Data.NewRecordResponse;
import ContractData = App.Data.ContractData;
import ContractRequestData = App.Data.ContractRequestData;
import ContractDataStoreRequest = App.Data.ContractDataStoreRequest;
import SavedRecordResponse = App.Data.SavedRecordResponse;
import ContractDataStatusUpdateRequest = App.Data.ContractDataStatusUpdateRequest;
import CarbonValueRequest = App.Data.CarbonValueRequest;
import ContractChangeData = App.Data.ContractChangeData;
import ContractChangeRequest = App.Data.ContractChangeRequest;
import SuggestedCarbonValueData = App.Data.SuggestedCarbonValueData;
import ContractMessageDataStoreRequest = App.Data.ContractMessageDataStoreRequest;

export const getContracts = (fields: ContractRequestData) => {
  return ApiCall.create(
    (options, query) => {
      return http.get<PaginatedResponse<ContractData>>(`contracts` + query);
    },
    {},
    '',
    fields,
  );
};

export const getCuratedOrganizationsContracts = () => {
  return ApiCall.create(
    (options, query) => {
      return http.get(`curatedOrganizationsContracts` + query);
    },
    {},
    '',
  );
};

export const getAllOrganizationsContracts = () => {
  return ApiCall.create(
    (options, query) => {
      return http.get(`allOrganizationsContracts` + query);
    },
    {},
    '',
  );
};

export const getContract = (id: string) => {
  const options = {} as KeyValue;
  options.id = id;
  return ApiCall.create(options => {
    return http.get<ContractData>(`/contracts/${options.id}`);
  }, options);
};

export const createContract = (fields: ContractRequestData) => {
  return ApiCall.create(
    (options, query, fields) => {
      return http.post<NewRecordResponse>('/contracts', fields);
    },
    {},
    '',
    fields,
  );
};

export const updateContract = (id: string, fields: ContractDataStoreRequest) => {
  const options = {} as KeyValue;
  options.id = id;
  return ApiCall.create(
    (options, query, fields) => {
      return http.patch<SavedRecordResponse>(`/contracts/${options.id}`, fields);
    },
    options,
    '',
    fields,
  );
};

export const toggleContractStatus = (id: string) => {
  const options = {} as KeyValue;
  options.id = id;
  return ApiCall.create(options => {
    return http.patch<SavedRecordResponse>(`/contracts/${options.id}/toggleContractStatus`);
  }, options);
};

export const deleteContract = (id: number) => {
  const options = {} as KeyValue;
  options.id = id.toString();
  return ApiCall.create(options => {
    return http.delete(`/contracts/${options.id}`);
  }, options);
};

// admin

export const getManagedContracts = (fields?: Array<FilterParam>) => {
  return ApiCall.create(
    (options, query) => {
      return http.get<PaginatedResponse<ContractData>>(`managed/contracts` + query);
    },
    {},
    '',
    fields,
  );
};

export const getManagedContract = (id: string) => {
  const options = {} as KeyValue;
  options.id = id;
  return ApiCall.create(options => {
    return http.get<ContractData>(`/managed/contracts/${options.id}`);
  }, options);
};

export const processContract = (id: string, status: string, fields: ContractDataStatusUpdateRequest) => {
  const options = {} as KeyValue;

  options.id = id;

  return ApiCall.create(
    (options, query, fields) => {
      return http.patch<ContractDataStatusUpdateRequest>(`/managed/contracts/${options.id}`, fields);
    },
    options,
    '',
    fields,
  );
};

export const getContractChanges = (contractId: string) => {
  const options = {} as KeyValue;
  options.contractId = contractId;

  return ApiCall.create(
    (options, query) => {
      return http.get<PaginatedResponse<ContractChangeData>>(
        `/managed/contracts/${options.contractId}/changes` + query,
      );
    },
    options,
    '',
  );
};

export const createContractChange = (contractId: string, fields: ContractChangeRequest) => {
  const options = {} as KeyValue;
  options.contractId = contractId;

  return ApiCall.create(
    (options, query, fields) => {
      return http.post<ContractChangeRequest>(`/managed/contracts/${options.contractId}/changes`, fields);
    },
    options,
    '',
    fields,
  );
};

export const updateContractChange = (id: string, contractId: string, fields: ContractChangeRequest) => {
  const options = {} as KeyValue;
  options.id = id;
  options.id = contractId;

  return ApiCall.create(
    (options, query, fields) => {
      return http.post<ContractChangeRequest>(`/managed/contracts/${options.contractId}/changes/${options.id}`, fields);
    },
    options,
    '',
    fields,
  );
};

export const deleteContractChange = (id: string, contractId: string) => {
  const options = {} as KeyValue;
  options.id = id;
  options.id = contractId;

  return ApiCall.create(options => {
    return http.delete<ContractChangeRequest>(`/managed/contracts/${options.contractId}/changes/${options.id}`);
  }, options);
};

export const getSuggestedCarbonValue = (contractId: string, contractedAssetId: string) => {
  const options = {} as KeyValue;
  options.contractId = contractId;
  options.contractedAssetId = contractedAssetId;

  return ApiCall.create(options => {
    return http.get<SuggestedCarbonValueData>(
      `/managed/contracts/${options.contractId}/contractedAssets/${options.contractedAssetId}/suggest`,
    );
  }, options);
};

export const createCarbonValueForContract = (contractId: string, contractedAssetId: string, value: number) => {
  const options = {} as KeyValue;
  options.contractId = contractId;
  options.contractedAssetId = contractedAssetId;

  const fields = {
    carbon_value: value,
  } as CarbonValueRequest;

  return ApiCall.create(
    (options, query, fields) => {
      return http.post<ContractChangeRequest>(
        `/managed/contracts/${options.contractId}/contractedAssets/${options.contractedAssetId}/carbon`,
        fields,
      );
    },
    options,
    '',
    fields,
  );
};

export const downloadActionAssetsCSV = (contractId: number, actionId: number) => {
  http
    .get(`/managed/contracts/${contractId}/contractedActions/${actionId}/export.xlsx`, {
      responseType: 'blob',
    })
    .then(response => {
      const blob = new Blob([response.data], {
          type: 'text/xlsx',
        }),
        url = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = response.headers['x-filename'];
      downloadLink.click();
    });
};

export const getOperatorContractMessages = (contractId: number, fields: ContractMessageRequestData) => {
  return ApiCall.create(
    (options, query) => {
      return http.get<PaginatedResponse<ContractData>>(`/managed/contracts/${contractId}/messages` + query);
    },
    {},
    '',
    fields,
  );
};

export const viewOperatorContractMessage = (contractId: number, contractMessageId: string) => {
  return ApiCall.create(
    (options, query) => {
      return http.get<ContractData>(`/managed/contracts/${contractId}/messages/${contractMessageId}` + query);
    },
    {},
    '',
    {},
  );
};

export const sendOperatorContractMessage = (contractId: number, contractMessageId) => {
  return ApiCall.create(
    (options, query) => {
      return http.get<ContractData>(`/managed/contracts/${contractId}/messages/${contractMessageId}/send` + query);
    },
    {},
    '',
    {},
  );
};

export const closeContractDiscussion = (contractId: number, contractMessageId) => {
  return ApiCall.create(
    (options, query) => {
      return http.get<ContractData>(`/managed/contracts/${contractId}/messages/${contractMessageId}/close` + query);
    },
    {},
    '',
    {},
  );
};

export const createOperatorContractMessage = (contractId: number, fields: ContractMessageDataStoreRequest) => {
  return ApiCall.create(
    (options, query, fields) => {
      return http.post<NewRecordResponse>(`/managed/contracts/${contractId}/messages`, fields);
    },
    {},
    '',
    fields,
  );
};

export const updateOperatorContractMessage = (contractId: number, id: string, fields: ContractMessageDataStoreRequest) => {
  const options = {} as KeyValue;
  options.id = id;
  options.contractId = contractId;
  return ApiCall.create(
    (options, query, fields) => {
      return http.patch<SavedRecordResponse>(`/managed/contracts/${options.contractId}/messages/${options.id}`, fields);
    },
    options,
    '',
    fields,
  );
};

export const deleteOperatorContractMessage = (contractId: number, id: string) => {
  const options = {} as KeyValue;
  options.messageId = id;
  options.contractId = contractId;
  return ApiCall.create(
    (options, query, fields) => {
      return http.delete<SavedRecordResponse>(`/managed/contracts/${options.contractId}/messages/${options.messageId}`, fields);
    },
    options,
    '',
    {},
  );
};

export const getContractMessages = (contractId: number, fields: ContractMessageRequestData) => {
  return ApiCall.create(
    (options, query) => {
      return http.get<PaginatedResponse<ContractData>>(`/contracts/${contractId}/messages` + query);
    },
    {},
    '',
    fields,
  );
};

export const viewContractMessage = (contractId: number, contractMessageId) => {
  return ApiCall.create(
    (options, query) => {
      return http.get<ContractData>(`/contracts/${contractId}/messages/${contractMessageId}` + query);
    },
    {},
    '',
    {},
  );
};

export const sendContractMessage = (contractId: number, contractMessageId) => {
  return ApiCall.create(
    (options, query) => {
      return http.get<ContractData>(`/contracts/${contractId}/messages/${contractMessageId}/send` + query);
    },
    {},
    '',
    {},
  );
};

export const createContractMessage = (contractId: number, fields: ContractMessageDataStoreRequest) => {
  return ApiCall.create(
    (options, query, fields) => {
      return http.post<NewRecordResponse>(`/contracts/${contractId}/messages`, fields);
    },
    {},
    '',
    fields,
  );
};

export const updateContractMessage = (contractId: number, id: string, fields: ContractMessageDataStoreRequest) => {
  const options = {} as KeyValue;
  options.id = id;
  options.contractId = contractId;
  return ApiCall.create(
    (options, query, fields) => {
      return http.patch<SavedRecordResponse>(`/contracts/${options.contractId}/messages/${options.id}`, fields);
    },
    options,
    '',
    fields,
  );
};

export const deleteContractMessage = (contractId: number, id: string) => {
  const options = {} as KeyValue;
  options.messageId = id;
  options.contractId = contractId;
  return ApiCall.create(
    (options, query, fields) => {
      return http.delete<SavedRecordResponse>(`/contracts/${options.contractId}/messages/${options.messageId}`, fields);
    },
    options,
    '',
    {},
  );
};
